import React, { useState, useEffect, useCallback } from 'react';
import { ChevronRight, ArrowUpDown, Loader2, MessageCircle, X, Code2, Activity } from 'lucide-react';
import AssetPicker from './AssetPicker';
import { fetchAssets, estimateSwap, createQuote } from '../services/api';
import TokenImage from './TokenImage';
import { useLanguage } from '../i18n/LanguageContext';

const TELEGRAM_LINK = 'https://t.me/iceswapio';
const TWITTER_LINK = 'https://x.com/iceswapio';
const API_DOCS_LINK = '/docs';
const EXPLORER_LINK = '/explorer';


const SwapForm = () => {
  const { t } = useLanguage();
  const [assets, setAssets] = useState(null);
  const [fromAsset, setFromAsset] = useState('BTC');
  const [toAsset, setToAsset] = useState('XMR');
  const [fromChain, setFromChain] = useState('Bitcoin');
  const [toChain, setToChain] = useState('Monero');
  const [amount, setAmount] = useState('');
  const [recipient, setRecipient] = useState('');
  const [refund, setRefund] = useState('');
  const [showSelector, setShowSelector] = useState(null);
  const [receiveAmount, setReceiveAmount] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadAssets = useCallback(async () => {
    try {
      const data = await fetchAssets();
      setAssets(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setAssetsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  const findChainForAsset = useCallback((symbol) => {
    if (!assets?.chains) return '';
    for (const [chainName, symbols] of Object.entries(assets.chains)) {
      if (symbols.includes(symbol)) return chainName;
    }
    return '';
  }, [assets]);

  const handleAssetSelect = (symbol, type) => {
    const chainName = findChainForAsset(symbol);
    if (type === 'from') {
      setFromAsset(symbol);
      setFromChain(chainName);
    } else {
      setToAsset(symbol);
      setToChain(chainName);
    }
    setReceiveAmount(null);
    setError(null);
    setShowSelector(null);
  };

  const flipAssets = () => {
    setFromAsset(toAsset);
    setToAsset(fromAsset);
    setFromChain(toChain);
    setToChain(fromChain);
    setReceiveAmount(null);
  };

  const getEstimate = useCallback(async () => {
    if (!amount || parseFloat(amount) <= 0) return;
    setEstimating(true);
    try {
      const result = await estimateSwap({
        from_asset: fromAsset,
        to_asset: toAsset,
        amount: parseFloat(amount),
      });
      setReceiveAmount(result.to_amount);
      setError(null);
    } catch (err) {
      setReceiveAmount(null);
      setError(err.message);
    } finally {
      setEstimating(false);
    }
  }, [amount, fromAsset, toAsset]);

  useEffect(() => {
    const timer = setTimeout(getEstimate, 500);
    return () => clearTimeout(timer);
  }, [getEstimate]);

  const handleSwap = async () => {
    if (!recipient || recipient.trim().length < 10) {
      setError(t('swapForm.enterAddressError', { asset: toAsset }));
      return;
    }
    const refundAddress = refund || recipient;
    setLoading(true);
    setError(null);
    try {
      const result = await createQuote({
        from_asset: fromAsset,
        to_asset: toAsset,
        amount: parseFloat(amount),
        recipient: recipient,
        refund: refundAddress,
      });
      window.location.href = `/order/${result.order_id}`;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (assetsLoading) {
    return (
      <div className="swap-container">
        <div className="card swap-card">
          <div className="loading" style={{ padding: '2rem' }}>
            <div className="spinner" />
            <p>{t('swapForm.loadingCoins')}</p>
          </div>
        </div>
      </div>
    );
  }

  const canSubmit = !!receiveAmount && !estimating;

  return (
    <div className="swap-container">
      <div className="card swap-card">
        <div className="field-pair">
          <div className="input-group">
            <label className="input-label">{t('common.youSend')}</label>
            <button className="asset-button" onClick={() => setShowSelector('from')}>
              <div className="asset-info">
                <TokenImage symbol={fromAsset} chain={fromChain} size={40} />
                <div className="asset-details">
                  <span className="asset-symbol">{fromAsset}</span>
                  <span className="asset-chain">{fromChain || t('common.chooseANetwork')}</span>
                </div>
              </div>
              <ChevronRight className="asset-arrow" size={18} />
            </button>
          </div>

          <div className="input-group" style={{ marginBottom: 0 }}>
            <label className="input-label">{t('common.youReceive')}</label>
            <button className="asset-button" onClick={() => setShowSelector('to')}>
              <div className="asset-info">
                <TokenImage symbol={toAsset} chain={toChain} size={40} />
                <div className="asset-details">
                  <span className="asset-symbol">{toAsset}</span>
                  <span className="asset-chain">{toChain || t('common.chooseANetwork')}</span>
                </div>
              </div>
              <ChevronRight className="asset-arrow" size={18} />
            </button>
          </div>

          <button className="swap-flip" onClick={flipAssets} aria-label={t('swapForm.flipAria')}>
            <ArrowUpDown size={16} />
          </button>
        </div>

        <div className="input-group" style={{ marginTop: '0.9rem' }}>
          <label className="input-label">{t('swapForm.amountLabel')}</label>
          <div className="amount-input-wrapper">
            <input
              type="number"
              inputMode="decimal"
              value={amount}
              onChange={(e) => { setAmount(e.target.value); setReceiveAmount(null); }}
              placeholder="0.0"
              className="amount-input"
            />
            <span className="amount-currency">{fromAsset}</span>
          </div>
        </div>

        {estimating && (
          <div className="status-message" style={{ margin: '0.5rem 0' }}>
            <Loader2 size={14} className="spin" style={{ verticalAlign: '-2px', marginRight: '0.35rem' }} />
            {t('swapForm.findingRate')}
          </div>
        )}

        {receiveAmount && !estimating && (
          <div className="receive-box">
            <span className="receive-label">{t('common.receiveEstimateLabel')}</span>
            <span className="receive-amount">{receiveAmount} {toAsset}</span>
          </div>
        )}

        {receiveAmount && (
          <>
            <div className="input-group">
              <label className="input-label">{t('swapForm.yourAddressLabel', { asset: toAsset })}</label>
              <input
                type="text"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder={t('swapForm.pasteAddressPlaceholder', { asset: toAsset })}
                className="address-input"
              />
              <p className="input-help">{t('swapForm.addressHelp', { asset: toAsset })}</p>
            </div>

            <div className="input-group">
              <label className="input-label">{t('common.refundAddressLabel')}</label>
              <input
                type="text"
                value={refund}
                onChange={(e) => setRefund(e.target.value)}
                placeholder={t('swapForm.refundPlaceholder', { asset: fromAsset })}
                className="address-input"
              />
              <p className="input-help">{t('swapForm.refundHelp', { asset: fromAsset })}</p>
            </div>
          </>
        )}

        {error && <div className="error-message">{error}</div>}

        <button
          className="btn btn-primary"
          onClick={canSubmit ? handleSwap : getEstimate}
          disabled={loading || estimating || !amount}
        >
          {loading ? t('swapForm.processing') : canSubmit ? t('common.exchangeNow') : t('common.getQuote')}
        </button>

        <div className="swap-footer-links">
          <a href={EXPLORER_LINK} className="footer-link explorer">
            <Activity size={14} /><span>Explorer</span>
          </a>
          <a href={TELEGRAM_LINK} target="_blank" rel="noopener noreferrer" className="footer-link telegram">
            <MessageCircle size={14} /><span>Support</span>
          </a>
          <a href={TWITTER_LINK} target="_blank" rel="noopener noreferrer" className="footer-link x-social">
            <X size={14} /><span>Updates</span>
          </a>
          <a href={API_DOCS_LINK} className="footer-link api">
            <Code2 size={14} /><span>API</span>
          </a>
        </div>
      </div>

      {showSelector && (
        <AssetPicker
          assets={assets}
          selected={showSelector === 'from' ? fromAsset : toAsset}
          onSelect={(symbol) => handleAssetSelect(symbol, showSelector)}
          onClose={() => setShowSelector(null)}
          title={showSelector === 'from' ? t('common.youSend') : t('common.youReceive')}
        />
      )}
    </div>
  );
};

export default SwapForm;